import { DashboardCard, SectionTitle } from '../ui'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMemo } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
} from 'recharts'

/* ===================== Helpers ===================== */
const GREEN = 'rgb(var(--success))'   // Resultado positivo
const RED   = 'rgb(var(--danger))'   // Resultado negativo
const BLUE  = 'rgb(var(--info))'   // Presupuesto
const COLOR_MUTED = 'rgb(var(--muted))'
const COLOR_GRID = 'rgb(var(--border))'

const RE_YM = /^\d{4}-\d{2}$/
const RE_YMD = /^\d{4}-\d{2}-\d{2}/
const RE_YM_COMPACT = /^\d{6}$/
const RE_MY = /^\d{2}-\d{4}$/
const RE_YM_SLASH = /^\d{4}\/\d{2}$/
const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
const MONTHS_SHORT_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

const formatCLP = (n) => Number(n ?? 0).toLocaleString('es-CL')

const pickPeriodValue = (value) => {
  if (!value) return ''
  if (typeof value === 'object') {
    return (
      value.name ??
      value.mes ??
      value.month ??
      value.period ??
      value.monthKey ??
      value.fecha ??
      value.date ??
      value.label ??
      value.labelShort ??
      value.key ??
      ''
    )
  }
  return value
}

const normalizePeriodKey = (value) => {
  const raw = pickPeriodValue(value)
  if (!raw) return ''
  if (raw instanceof Date) return raw.toISOString().slice(0, 7)
  const s = String(raw)
  if (RE_YM.test(s)) return s
  if (RE_YMD.test(s)) return s.slice(0, 7)
  if (RE_YM_COMPACT.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}`
  if (RE_YM_SLASH.test(s)) return s.replace('/', '-')
  if (RE_MY.test(s)) {
    const [m, y] = s.split('-')
    return `${y}-${m}`
  }
  return s
}

const formatMonthLabel = (value, style = 'long') => {
  const key = normalizePeriodKey(value)
  if (!key) return ''
  if (RE_YM.test(key)) {
    const monthIndex = parseInt(key.slice(5, 7), 10) - 1
    const list = style === 'short' ? MONTHS_SHORT_ES : MONTHS_ES
    return list[monthIndex] ?? key
  }
  return String(key)
}

// label “Abril” desde “YYYY-MM”
const monthLabel = (ym) => formatMonthLabel(ym, 'long')

// ticks Y en K/M
const formatAxis = (v) =>
  v >= 1_000_000 ? (v / 1_000_000).toFixed(1) + 'M'
  : v >= 1_000    ? (v / 1_000).toFixed(0) + 'K'
  : v.toLocaleString('es-CL')

/* ===================== Tooltip estilo tarjeta ===================== */
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  const p = payload[0]?.payload || {}
  const month = p.periodo || p.mes || p.label
  const originalReal   = Number(p.originalReal || 0)
  const originalBudget = Number(p.originalBudget || 0)
  const diff           = originalReal - originalBudget
  const realColor = originalReal < 0 ? RED : GREEN
  const signReal  = originalReal > 0 ? '+' : ''
  const signDiff  = diff > 0 ? '+' : ''

  return (
    <div className='bg-surface rounded-2xl shadow-card border border-border px-4 py-3 text-text'>
      <div className='font-extrabold text-lg mb-1'>{monthLabel(month)}</div>

      {/* Real */}
      <div className='flex items-center gap-2'>
        <span
          className='inline-block w-2.5 h-2.5 rounded-full'
          style={{ backgroundColor: realColor }}
        />
        <span className='font-semibold' style={{ color: realColor }}>
          Real:
        </span>
        <span className='ml-1 font-medium'>
          {signReal}
          {formatCLP(originalReal)}
        </span>
      </div>

      {/* Presupuesto */}
      <div className='flex items-center gap-2 mt-1'>
        <span className='inline-block w-2.5 h-2.5 rounded-full' style={{ backgroundColor: BLUE }} />
        <span className='font-semibold' style={{ color: BLUE }}>
          Presupuestado:
        </span>
        <span className='ml-1 font-medium'>{formatCLP(originalBudget)}</span>
      </div>

      {/* Separador */}
      <div className='h-px bg-border my-2' />

      {/* Diferencia */}
      <div className='flex items-center gap-2'>
        <span className='font-semibold'>Diferencia:</span>
        <span className='ml-1 font-medium'>
          {signDiff}
          {formatCLP(diff)}
        </span>
      </div>
    </div>
  )
}



/* ===================== Componente ===================== */
const MirrorChart = ({ chartData=[] }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'))
  const showMobileLabels = isMobile && Array.isArray(chartData) && chartData.length > 0
  const showDesktopLabels = !isMobile && Array.isArray(chartData) && chartData.length > 0
  const normalizedData = useMemo(() => {
    const src = Array.isArray(chartData) ? chartData : []
    return src.map(item => {
      const periodKey = normalizePeriodKey(
        item?.name ??
        item?.mes ??
        item?.month ??
        item?.period ??
        item?.monthKey ??
        item?.fecha ??
        item?.date ??
        item?.label ??
        item?.labelShort ??
        item?.key
      )
      const fallbackLabel = String(item?.label ?? item?.labelShort ?? item?.name ?? '')
      const label = formatMonthLabel(periodKey, 'long') || fallbackLabel
      const labelShort = formatMonthLabel(periodKey, 'short') || fallbackLabel
      return {
        ...item,
        name: periodKey || String(item?.name ?? ''),
        label,
        labelShort,
      }
    })
  }, [chartData])
  const displayData = useMemo(() => {
    if (!isMobile) return normalizedData
    return normalizedData.slice(-3)
  }, [normalizedData, isMobile])
  const renderMonthTick = (props) => {
    const { x, y, payload } = props || {}
    const label = formatMonthLabel(payload?.value, 'short')
    if (!label) return null
    return (
      <text
        x={x}
        y={(y ?? 0) + 12}
        textAnchor="middle"
        fill={COLOR_MUTED}
        fontSize={isMobile ? 11 : 13}
      >
        {label}
      </text>
    )
  }
  const chartHeight = isMobile ? 250 : isTablet ? 320 : 400
  return (
    <DashboardCard className='flex h-full min-w-0 flex-col'>
      {/* Título */}
      <div className='flex flex-col justify-center items-center pb-4'>
        <SectionTitle className='text-center'>Real VS Presupuesto</SectionTitle>
      </div>

      <div className='w-full min-w-0' style={{ height: chartHeight }}>
        <ResponsiveContainer width='100%' height='100%'>
        <BarChart
          data={displayData}
          layout='horizontal'
          margin={isMobile ? { top: 12, right: 8, left: 8, bottom: 28 } : { top: 10, right: 3, left: 3, bottom: 24 }}
          barCategoryGap={isMobile ? '20%' : undefined}
        >
          <CartesianGrid strokeDasharray='1 1' vertical={false} stroke={COLOR_GRID} />
          <>
            <XAxis
              dataKey='name'
              ticks={displayData.map(item => item.name)}
              tick={isMobile ? renderMonthTick : false}
              axisLine={false}
              tickLine={false}
              height={isMobile ? 22 : 8}
              tickMargin={0}
              interval={0}
              minTickGap={0}
              allowDuplicatedCategory={false}
            />
            <YAxis
              tick={{ fontSize: isMobile ? 11 : 14, fill: COLOR_MUTED }}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatAxis}
            />
          </>

          {/* Tooltip estilo tarjeta */}
          <Tooltip cursor={{ fill: 'rgb(var(--border) / 0.35)' }} content={<CustomTooltip />} />

          {/* Barras */}
          <Bar dataKey='total' name='Real' radius={[4, 4, 0, 0]}>
            {displayData.map((e, i) => (
              <Cell key={i} fill={e.originalReal < 0 ? RED : GREEN} />
            ))}
          </Bar>
          <Bar dataKey='budget' fill={BLUE} name='Presupuesto' radius={[4, 4, 0, 0]} />
        </BarChart>
        </ResponsiveContainer>
      </div>
      {showMobileLabels && (
        <div
          className='mt-2 grid text-xs text-muted'
          style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
        >
          {displayData.map(item => (
            <div key={item.name} className='text-center'>
              {item.labelShort || item.label || formatMonthLabel(item.name, 'short')}
            </div>
          ))}
        </div>
      )}
      {showDesktopLabels && (
        <div
          className='mt-2 grid text-xs text-muted'
          style={{ gridTemplateColumns: `repeat(${displayData.length}, minmax(0, 1fr))` }}
        >
          {displayData.map(item => (
            <div key={item.name} className='text-center'>
              {item.labelShort || item.label || formatMonthLabel(item.name, 'short')}
            </div>
          ))}
        </div>
      )}
    </DashboardCard>
  )
}

export default MirrorChart
